import { Composer } from "@somethings-ui/web";
import { useGroupChannelContext } from "@sendbird/uikit-react/GroupChannel/context";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { z } from "zod";
import { fetchApi } from "@/lib/api";
import { useAISuggestionFeedback } from "@/lib/hooks/ai/use-ai-suggestion-feedback";
import type { AttentionSuggestionType } from "./attention-analytics";

const GENERATE_APPROACHES = [
  "Validate",
  "Offer Advice / Encouragement",
  "Share Lived Experience",
  "Ask a Question",
  "Lighten the Mood / Build Connection",
] as const;

const suggestedReplySchema = z.union([
  z.object({
    type: z.literal("default"),
    data: z.object({
      statement: z.string(),
      question: z.string(),
      reasoning: z.string(),
    }),
  }),
  z.object({
    type: z.literal("specific"),
    data: z.object({ draft: z.string(), reasoning: z.string() }),
  }),
  z.object({
    type: z.literal("priority"),
    data: z.object({
      draft: z.string(),
      reasoning: z.string(),
      nextAction: z.string().optional(),
    }),
  }),
]);

type SuggestedReply = z.infer<typeof suggestedReplySchema>;

interface UseSuggestedReplyArgs {
  mentorshipId: string | null;
  lastMessageId: string | null;
  quotedText: string | null;
  style: string | null;
  // Bumped on each regenerate to force a fresh fetch (part of the query key).
  regenerationCount: number;
  enabled: boolean;
}

function useSuggestedReply({
  mentorshipId,
  lastMessageId,
  quotedText,
  style,
  regenerationCount,
  enabled,
}: UseSuggestedReplyArgs) {
  return useQuery<SuggestedReply | null>({
    queryKey: [
      "attention-suggested-replies",
      mentorshipId,
      lastMessageId,
      quotedText,
      style,
      regenerationCount,
    ],
    queryFn: async () => {
      const { data } = await fetchApi<unknown>({
        url: `v2/messages/${mentorshipId}/suggested-replies`,
        method: "GET",
        params: {
          force: regenerationCount > 0 || style !== null,
          suggestionStyle: style ?? undefined,
          selectedMessageText: quotedText ?? undefined,
        },
      });
      const parsed = suggestedReplySchema.safeParse(data);
      return parsed.success ? parsed.data : null;
    },
    enabled: enabled && Boolean(mentorshipId),
    staleTime: Infinity,
  });
}

export interface AttentionSuggestionsProps {
  mentorshipId: string | null;
  userId: string;
  quotedText?: string | null;
  /** Insert a chosen suggestion into the composer draft without clobbering it. */
  onInsert: (
    text: string,
    suggestionType: AttentionSuggestionType,
    approach: string,
  ) => void;
  /** Analytics seam fired when the mentor regenerates a suggestion. */
  onRegenerate?: (
    suggestionType: AttentionSuggestionType | null,
    approach: string | null,
  ) => void;
}

// Suggested-messages surface behind the composer Lightbulb. Output is inserted via
// the composer's append-only `onInsert`, so existing draft text is preserved.
export function AttentionSuggestions({
  mentorshipId,
  userId,
  quotedText = null,
  onInsert,
  onRegenerate,
}: AttentionSuggestionsProps) {
  const { currentChannel } = useGroupChannelContext();
  const lastMessage = currentChannel?.lastMessage ?? null;
  const lastMessageId = lastMessage ? String(lastMessage.messageId) : null;
  const lastSenderId =
    (lastMessage as { sender?: { userId?: string } } | null)?.sender?.userId ??
    null;
  const isLastMessageFromMentee =
    lastSenderId !== null && lastSenderId !== userId;

  const [style, setStyle] = useState<string | null>(null);
  const [showApproach, setShowApproach] = useState(!isLastMessageFromMentee);
  const [regenerationCount, setRegenerationCount] = useState(0);
  const [reasoningOpen, setReasoningOpen] = useState(false);

  const feedback = useAISuggestionFeedback({
    mentorshipId: mentorshipId ?? undefined,
    userId,
  });

  const {
    data: suggestion,
    isFetching,
    isSuccess,
  } = useSuggestedReply({
    mentorshipId,
    lastMessageId,
    quotedText,
    style,
    regenerationCount,
    enabled: !showApproach,
  });

  const handleInsert = (
    text: string,
    suggestionType: AttentionSuggestionType,
    approach: string,
  ) => {
    feedback.userClickedSuggestion({
      text,
      approach,
      triggeringMessageId: lastMessageId,
      suggestion_type: suggestionType,
      quoted_message_text: quotedText ?? undefined,
    });
    onInsert(text, suggestionType, approach);
  };

  const handleRegenerate = (
    text: string,
    suggestionType: AttentionSuggestionType,
    approach: string,
  ) => {
    feedback.userDiscardedVisibleSuggestion({
      text,
      approach,
      triggeringMessageId: lastMessageId,
      suggestion_type: suggestionType,
      quoted_message_text: quotedText ?? undefined,
    });
    onRegenerate?.(suggestionType, approach);
    setRegenerationCount((prev) => prev + 1);
  };

  const backToApproaches = () => {
    setShowApproach(true);
    setStyle(null);
  };

  if (showApproach) {
    return (
      <Composer.Suggestions header="Generate a message" variant="pills">
        {GENERATE_APPROACHES.map((approach) => (
          <Composer.SuggestionPill
            key={approach}
            onClick={() => {
              setStyle(approach);
              setShowApproach(false);
              setRegenerationCount(0);
            }}
          >
            {approach}
          </Composer.SuggestionPill>
        ))}
      </Composer.Suggestions>
    );
  }

  if (!suggestion) {
    // Only a settled, empty result is a genuine "no suggestion"; otherwise loader.
    const resolvedEmpty = isSuccess && !isFetching;
    return (
      <Composer.Suggestions header="Suggested message">
        <div className="flex w-full items-center justify-center gap-2 py-2 text-text-secondary">
          {resolvedEmpty ? (
            <span className="font-body text-body-2">
              No suggestion available.
            </span>
          ) : (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              <span className="font-body text-body-2">Crafting reply...</span>
            </>
          )}
        </div>
      </Composer.Suggestions>
    );
  }

  const reasoningNode = (
    <span className="font-body text-body-2 text-text-secondary">
      {suggestion.data.reasoning}
    </span>
  );

  const panelProps = {
    reasoning: reasoningNode,
    reasoningOpen,
    onReasoningToggle: () => setReasoningOpen((prev) => !prev),
    onTryAnother: backToApproaches,
  };

  if (suggestion.type === "default") {
    const { statement, question } = suggestion.data;
    return (
      <SuggestedReplyPanel header="Suggested message" {...panelProps}>
        <Composer.SuggestionCard
          label="Statement"
          onRegenerate={() =>
            handleRegenerate(statement, "default", "statement")
          }
          role="button"
          tabIndex={0}
          className="cursor-pointer"
          onClick={() => handleInsert(statement, "default", "statement")}
        >
          {statement}
        </Composer.SuggestionCard>
        <Composer.SuggestionCard
          label="Question"
          onRegenerate={() => handleRegenerate(question, "default", "question")}
          role="button"
          tabIndex={0}
          className="cursor-pointer"
          onClick={() => handleInsert(question, "default", "question")}
        >
          {question}
        </Composer.SuggestionCard>
      </SuggestedReplyPanel>
    );
  }

  const { draft } = suggestion.data;
  const approach = style ?? suggestion.type;
  return (
    <SuggestedReplyPanel
      header={suggestion.type === "priority" ? "Priority reply" : "Suggested reply"}
      {...panelProps}
    >
      <Composer.SuggestionCard
        label={suggestion.type === "priority" ? "Priority" : "Suggested reply"}
        onRegenerate={() => handleRegenerate(draft, suggestion.type, approach)}
        role="button"
        tabIndex={0}
        className="cursor-pointer"
        onClick={() => handleInsert(draft, suggestion.type, approach)}
      >
        {draft}
      </Composer.SuggestionCard>
    </SuggestedReplyPanel>
  );
}

interface SuggestedReplyPanelProps {
  header: string;
  reasoning: ReactNode;
  reasoningOpen: boolean;
  onReasoningToggle: () => void;
  onTryAnother: () => void;
  children: ReactNode;
}

// Suggestion cards followed by a centered "try a different approach" action row.
function SuggestedReplyPanel({
  header,
  reasoning,
  reasoningOpen,
  onReasoningToggle,
  onTryAnother,
  children,
}: SuggestedReplyPanelProps) {
  return (
    <div className="flex flex-col">
      <Composer.Suggestions
        header={header}
        reasoning={reasoning}
        reasoningOpen={reasoningOpen}
        onReasoningToggle={onReasoningToggle}
        className="pb-0"
      >
        {children}
      </Composer.Suggestions>
      <div className="flex justify-center px-4 pb-4 pt-3">
        <Composer.SuggestionPill onClick={onTryAnother}>
          Try a different approach
        </Composer.SuggestionPill>
      </div>
    </div>
  );
}
